/**
 * Renders the CommentRpaNewModal and HappyFlowModal conditionally
 * based on the currently opened modal in Redux state.
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CommentRpaNewModal from "../../../../components/modals/commentRPAnewModal/commentRpaNewModal";
import HappyFlowModal from "../../../../components/modals/happyFlowModal/happyFlowModal";
import { ToastMessages } from "../../../../components/toasts/toastMessages";
import { useBusniessOfficeNotesContext } from "../../../../context/busniessOfficeNotesContext";
import { AllCommentsProps } from "../../../../data-model/busniessOfficeNote";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../../data-model/medicalPaOrdersModel";
import { ModalId } from "../../../../enums/modalId";
import { UserGeneralInfoContextKeys } from "../../../../enums/userGeneralInfoContextKeys";
import { closeModal } from "../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import { UserStore } from "../../../../store/UserGeneralInfoStore";
import { shouldShowReassignmentCheckbox } from "../../../../utils/organizationHelper";
import { CoverageAnalytics } from "./hooks/useCoverageAnalytics";
import { HappyFlowSave } from "./hooks/useHappyFlowSave";

/** Props for the CoverageModals component. */
interface CoverageModalsProps {
  analytics: CoverageAnalytics;
  happyFlow: HappyFlowSave;
  writeBackNotes: AllCommentsProps;
  generalOrderDetails?: GeneralOrderDetails;
  authOnFileDetails: AuthOnFile;
}

/**
 * Conditionally renders the CommentRpaNewModal and HappyFlowModal.
 * The CommentRpa save handler fires analytics, logs J-codes, closes
 * the modal, and navigates. The HappyFlow delegates to its dedicated
 * save hook.
 *
 * @param props - Analytics hook, happy flow save hook, and write-back notes
 * @returns The modals JSX or null when no modal is open
 */
const CoverageModals = ({
  analytics,
  happyFlow,
  writeBackNotes,
  generalOrderDetails,
  authOnFileDetails,
}: CoverageModalsProps) => {
  const { openedModalId } = useSelector((state: RootState) => state.modal);
  const { manualAuthStatus } = useBusniessOfficeNotesContext();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  /** Fires analytics, logs J-codes, closes the modal, and navigates to worklists. */
  const handleCommentRpaSave = () => {
    analytics.fireMarkAsCompletedEvent(
      analytics.ScreenNamesForBigQuery.COVERAGE_SCREEN,
      analytics.ModalNamesForBigQuery.COMMENT_RPA_MODAL_NEW,
      analytics.oldAuthStatusFromContext,
    );
    analytics.logJCodesAfterSave(1000);
    dispatch(closeModal());
    toast.success(ToastMessages.MARK_AS_COMPLETED_SUCCESS);
    navigate(
      `/worklists/${UserStore.getItem(UserGeneralInfoContextKeys.PARENT_TABLE) ?? ""}`,
    );
  };

  return (
    <>
      {openedModalId === ModalId.COMMENT_RPA_MODAL_NEW && (
        <CommentRpaNewModal
          boStatus={happyFlow.boStatus}
          onClose={() => dispatch(closeModal())}
          onSave={handleCommentRpaSave}
          wrtiteBackStatus={
            generalOrderDetails?.status?.ev_write_back_status ?? ""
          }
          docRpaStatus={""}
          id={generalOrderDetails?.order_id ?? ""}
          orderId={generalOrderDetails?.order_id ?? ""}
          writeBackReports={[writeBackNotes]}
          auth_status={manualAuthStatus || authOnFileDetails?.auth_status || ""}
          generalOrderDetails={generalOrderDetails}
          authOnFileDetails={authOnFileDetails}
        />
      )}
      {openedModalId === ModalId.HAPPY_FLOW_MODAL && (
        <HappyFlowModal
          onClose={() => dispatch(closeModal())}
          onSave={happyFlow.handleSave}
          isLoading={happyFlow.isLoading}
          showReassignmentCheckbox={shouldShowReassignmentCheckbox(
            authOnFileDetails?.auth_status,
          )}
          onSelectedProviderChange={(provider) => {
            happyFlow.selectedProvider.current = provider;
          }}
          authStatus={manualAuthStatus || authOnFileDetails?.auth_status || ""}
        />
      )}
    </>
  );
};

export default CoverageModals;
